import { useLocalStorage } from "@mantine/hooks";
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import { useLocation } from "@tanstack/react-router";
import { useMemo } from "react";

import { api } from "@/api.ts";

const POLL_INTERVAL_MS = 5 * 60 * 1000;

export type ActiveNewsPost = {
  id: number;
  level: "info" | "warning";
  pinned: boolean;
  show_from: string | null;
  show_to: string | null;
  title: string;
  body: string;
  page_prefixes: Array<string> | null;
};

function isWithinShowWindow(post: ActiveNewsPost, now: number): boolean {
  if (post.show_from && new Date(post.show_from).getTime() > now) {
    return false;
  }
  if (post.show_to && new Date(post.show_to).getTime() < now) {
    return false;
  }
  return true;
}

/**
 * Checks whether `pathname` matches a single page prefix. A prefix of
 * exactly "/" is special-cased to match only the front page exactly
 * (rather than acting as a wildcard prefix matching everything); any other
 * prefix matches itself exactly or any sub-path beneath it.
 */
function matchesPagePrefix(pathname: string, prefix: string): boolean {
  if (prefix === "/") {
    return pathname === "/";
  }
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function isShownOnPage(post: ActiveNewsPost, pathname: string): boolean {
  if (post.page_prefixes == null) {
    return true;
  }
  return post.page_prefixes.some((prefix) =>
    matchesPagePrefix(pathname, prefix),
  );
}

/**
 * Fetches currently published news posts and exposes which ones are
 * "active" (within their show window and not dismissed). Dismissals are
 * tracked per-browser in localStorage, not server-side.
 */
export function useActiveNews() {
  const [dismissedIds, setDismissedIds] = useLocalStorage<Array<number>>({
    key: "news-dismissed",
    defaultValue: [],
    getInitialValueInEffect: false,
  });

  const { pathname } = useLocation();

  const { data } = useQuery(
    useMemo(
      () =>
        api
          .from("news_post")
          .select("id,level,pinned,show_from,show_to,title,body,page_prefixes")
          .eq("published", true)
          .order("pinned", { ascending: false })
          .order("show_from", { ascending: false }),
      [],
    ),
    { refetchInterval: POLL_INTERVAL_MS },
  );

  const activePosts = useMemo(() => {
    const now = Date.now();
    return ((data ?? []) as Array<ActiveNewsPost>).filter(
      (post) =>
        isWithinShowWindow(post, now) &&
        isShownOnPage(post, pathname) &&
        !dismissedIds.includes(post.id),
    );
  }, [data, dismissedIds, pathname]);

  const dismiss = (id: number) => {
    setDismissedIds((current) =>
      current.includes(id) ? current : [...current, id],
    );
  };

  return { activePosts, dismiss };
}
